import { roundTo2Decimals } from '@principle-theorem/accounting';
import { Invoice, Transaction } from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  TransactionStatus,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import { sortTimestampAsc } from '@principle-theorem/shared';
import { sumBy } from 'lodash';
import {
  ExactV2AdjustmentType,
  ExactV2TransactionType,
} from '../../../lib/exact-v2-transaction-type';
import { type IExactV2Transaction } from '../../../source/entities/patient-transactions-v2';
import { type IInvoiceBuildData } from './exact-invoice-builder-v2';
import {
  EXACT_MINIMUM_OWING,
  ExactTransactionLedger,
} from './exact-transaction-ledger';

/**
 * Exact settles by amount against any open row, not per-invoice, so credit
 * sits open naming no charge. `amountOpen` is what is left unspent on a row.
 */
export class ExactOpenCredit {
  constructor(
    private _transactions: IExactV2Transaction[],
    private _patientBalance: number
  ) {}

  /**
   * Money the patient paid in that Exact still holds open. It belongs to the
   * patient, so it can settle any invoice or be refunded.
   */
  static isRefundableCredit(transaction: IExactV2Transaction): boolean {
    return (
      transaction.amountOpen < 0 &&
      (transaction.transactionType === ExactV2TransactionType.Receipt ||
        transaction.transactionType === ExactV2TransactionType.Charge ||
        transaction.transactionType === ExactV2TransactionType.TransferIn ||
        transaction.transactionType === ExactV2TransactionType.TransferOut ||
        ExactTransactionLedger.isReceiptSideAdjustment(transaction))
    );
  }

  /**
   * A discount or write-down Exact granted but never allocated. It only
   * reduces what is owed, so it is never refundable.
   */
  static isUnallocatedDiscount(transaction: IExactV2Transaction): boolean {
    const isDiscountRow =
      transaction.transactionType === ExactV2TransactionType.Discount ||
      (transaction.transactionType === ExactV2TransactionType.Adjustment &&
        transaction.adjustmentType !== ExactV2AdjustmentType.ReceiptSide);
    return isDiscountRow && transaction.amountOpen <= -EXACT_MINIMUM_OWING;
  }

  /** Refundable credit pays first; a discount must not settle what it could. */
  spend(invoices: IInvoiceBuildData[]): void {
    this._spendPool(
      invoices,
      this._availableRefundableCredit(),
      TransactionProvider.Manual,
      'account-credit',
      'Account credit (migrated)'
    );
    this._spendPool(
      invoices,
      this._spendableDiscount(invoices),
      TransactionProvider.Discount,
      'unallocated-adjustment',
      'Exact write-off (migrated)'
    );
  }

  /** The credit balance is reserved — it becomes an AccountCredit. */
  private _availableRefundableCredit(): number {
    const reserved = roundTo2Decimals(Math.max(0, -this._patientBalance));
    return roundTo2Decimals(
      this._pool(ExactOpenCredit.isRefundableCredit) - reserved
    );
  }

  /**
   * `debtacc3.balance` sums every `amountOpen`, so Exact already counts an
   * open discount and we do not. Past that difference is debt Exact holds.
   */
  private _spendableDiscount(invoices: IInvoiceBuildData[]): number {
    const creditNoteTotal = sumBy(
      invoices.filter((invoiceData) => this._holdsCreditNote(invoiceData)),
      (invoiceData) => Math.abs(this._outstanding(invoiceData))
    );
    const invoiceDebt = sumBy(
      invoices.filter((invoiceData) => !this._holdsCreditNote(invoiceData)),
      (invoiceData) => Math.max(0, this._outstanding(invoiceData))
    );
    const debtExactHasNotDiscounted = roundTo2Decimals(
      invoiceDebt - creditNoteTotal - Math.max(0, this._patientBalance)
    );
    return Math.min(
      this._pool(ExactOpenCredit.isUnallocatedDiscount),
      debtExactHasNotDiscounted
    );
  }

  private _pool(
    matches: (transaction: IExactV2Transaction) => boolean
  ): number {
    return roundTo2Decimals(
      sumBy(this._transactions.filter(matches), (transaction) =>
        Math.abs(transaction.amountOpen)
      )
    );
  }

  private _spendPool(
    invoices: IInvoiceBuildData[],
    total: number,
    provider: TransactionProvider,
    uidPrefix: string,
    description: string
  ): void {
    let remaining = total;
    if (remaining < EXACT_MINIMUM_OWING) {
      return;
    }

    const owingInvoices = this._sortOldestFirst(
      invoices.filter((invoiceData) => this._outstanding(invoiceData) > 0)
    );

    for (const invoiceData of owingInvoices) {
      const applied = roundTo2Decimals(
        Math.min(this._outstanding(invoiceData), remaining)
      );
      if (applied <= 0) {
        continue;
      }

      invoiceData.transactions.push({
        ...Transaction.init({
          ...Transaction.internalReference(
            provider,
            `${uidPrefix}-${invoiceData.invoice.uid}`
          ),
          reference: description,
          type: TransactionType.Incoming,
          status: TransactionStatus.Complete,
          from: '',
          to: '',
          amount: applied,
          createdAt: invoiceData.invoice.createdAt,
          practiceRef: invoiceData.invoice.practice.ref,
          description,
        }),
      });

      remaining = roundTo2Decimals(remaining - applied);
      if (remaining < EXACT_MINIMUM_OWING) {
        return;
      }
    }
  }

  private _sortOldestFirst(invoices: IInvoiceBuildData[]): IInvoiceBuildData[] {
    return [...invoices].sort((aInvoice, bInvoice) =>
      sortTimestampAsc(aInvoice.invoice.createdAt, bInvoice.invoice.createdAt)
    );
  }

  private _holdsCreditNote({
    invoice,
    transactions,
  }: IInvoiceBuildData): boolean {
    return (
      roundTo2Decimals(Invoice.total(invoice)) < 0 &&
      roundTo2Decimals(Invoice.balance(invoice, transactions)) < 0
    );
  }

  private _outstanding(invoiceData: IInvoiceBuildData): number {
    const pendingTotal = sumBy(
      invoiceData.transactions.filter(
        (transaction) => transaction.status === TransactionStatus.Pending
      ),
      (transaction) => transaction.amount
    );
    return roundTo2Decimals(
      Invoice.balance(invoiceData.invoice, invoiceData.transactions) -
        pendingTotal
    );
  }
}
